/** @file Local setup that puts a bone at a world-space or image-pixel pose (bone.setTransform space, C6). */
import { docSetupWorlds } from '@pose2d/core/pose/docSetupWorlds';
import { imagePlacement } from '@pose2d/format/doc/imagePlacement';
import { throwError } from '@pose2d/format/throwError';
import type { Doc, Id, LocalTransform } from '@pose2d/format/types';
import { affine, type Affine } from '@pose2d/math/affine';
import { invariant } from '@pose2d/std/invariant';
import { localFromWorld } from './localFromWorld';
import { snapSetup } from './snapSetup';

/** The fields a setTransform value may carry. */
type Fields = { x?: number | undefined; y?: number | undefined; rotation?: number | undefined; scaleX?: number | undefined; scaleY?: number | undefined; shearX?: number | undefined; shearY?: number | undefined };

const LINEAR = ['rotation', 'scaleX', 'scaleY', 'shearX', 'shearY'] as const;

/** The skeleton point `value` names: image pixels through `toSkeleton` when given; an omitted coordinate keeps the origin's. */
function targetPoint(world: Affine, value: Fields, toSkeleton: Affine | null): [number, number] {
  const at: [number, number] = [world[4] ?? 0, world[5] ?? 0];
  if (toSkeleton === null) return [value.x ?? at[0], value.y ?? at[1]];
  affine.apply(affine.invert([0, 0, 0, 0, 0, 0], toSkeleton), at[0], at[1], at);
  return affine.apply(toSkeleton, value.x ?? at[0], value.y ?? at[1], [0, 0]);
}

/** Only the local x/y change: the parent's full world maps the local position for every inherit mode (§6.2). */
function movedOrigin(setup: LocalTransform, parentWorld: Affine, point: [number, number]): LocalTransform {
  const inverse = affine.invert([0, 0, 0, 0, 0, 0], parentWorld);
  if (!inverse.every(Number.isFinite)) {
    throwError('VALIDATION', undefined, { path: 'value', hint: 'the parent has a zero setup scale, so no local position reaches this point' });
  }
  const local = affine.apply(inverse, point[0], point[1], [0, 0]);
  return { ...setup, x: snapSetup(local[0]), y: snapSetup(local[1]) };
}

/** The world pose to reach: the given fields over the bone's current world (normal form), at `point`. */
function mergedWorld(current: LocalTransform, value: Fields, point: [number, number]): LocalTransform {
  return {
    x: point[0],
    y: point[1],
    rotation: value.rotation ?? current.rotation,
    scaleX: value.scaleX ?? current.scaleX,
    scaleY: value.scaleY ?? current.scaleY,
    shearX: value.shearX ?? current.shearX,
    shearY: value.shearY ?? current.shearY,
  };
}

/** A whole world pose through localFromWorld; VALIDATION at `space` unless the bone inherits normally (W = P · L). */
function posedWorld(doc: Doc, id: Id, parentWorld: Affine, world: Affine, value: Fields, point: [number, number]): LocalTransform {
  const bone = doc.bones[id];
  if (bone !== undefined && bone.inherit !== 'normal') {
    throwError('VALIDATION', undefined, { path: 'space', hint: `bone ${bone.name} uses inherit "${bone.inherit}"; a world rotation, scale or shear needs inherit "normal" (set it first, or send x/y only)` });
  }
  const w = mergedWorld(localFromWorld([1, 0, 0, 1, 0, 0], world, 'value'), value, point);
  return localFromWorld(parentWorld, affine.fromLocal([0, 0, 0, 0, 0, 0], w.x, w.y, w.rotation, w.scaleX, w.scaleY, w.shearX, w.shearY), 'value');
}

/**
 * The local setup of bone `id` for a `bone.setTransform` value in world space (`image` null: skeleton space, y up,
 * degrees CCW, the fields of affine.decompose of its setup world) or in the pixels of image `image` (x/y only, through
 * imagePlacement). x/y alone move only the bone origin and keep the other six fields as stored; any other field
 * rebuilds the whole local transform in normal form (shearX 0, a reflection as negative scaleY). Worlds are FK setup
 * worlds (docSetupWorlds, W2-14). VALIDATION at `value.<field>` for a rotation, scale or shear in image space.
 */
export function setupFromSpace(doc: Doc, id: Id, value: Fields, image: Id | null): LocalTransform {
  const bone = doc.bones[id];
  invariant(bone !== undefined, 'setupFromSpace: the bone was resolved');
  const linear = LINEAR.filter((field) => value[field] !== undefined);
  const [first] = linear;
  if (image !== null && first !== undefined) {
    throwError('VALIDATION', undefined, { path: `value.${first}`, hint: 'space "image" places the bone origin only (x, y in image pixels); send rotation, scale and shear with space "world" or "local"' });
  }
  const worlds = docSetupWorlds(doc);
  const world = worlds.get(id);
  const parentWorld = bone.parent === null ? [1, 0, 0, 1, 0, 0] : worlds.get(bone.parent);
  invariant(world !== undefined && parentWorld !== undefined, 'docSetupWorlds has a world for every bone');
  const toSkeleton = image === null ? null : (imagePlacement(doc).get(image) ?? null);
  const point = targetPoint(world, value, toSkeleton);
  return linear.length === 0 ? movedOrigin(bone.setup, parentWorld, point) : posedWorld(doc, id, parentWorld, world, value, point);
}
